//* Import the from libraries
import React, { useState, useCallback, useEffect } from "react";

//* Import hooks from react-router-dom
import { useNavigate } from "react-router-dom";

//* Import the List component
import { List } from "../../../component/list/list";

//* Import the Loading component
import { Loading } from "../../../component/common/Loading";

//* Import the useCollaborators hook
import { useCollaborator } from "../../../hooks/collaborators/useCollaborator";

//* Import the AuthAPI service
import { AuthAPI } from "../../../services/apis/AuthAPI";

//* Import the UserRole enum
import { UserRole } from "../../../constants/enum/UserRole";

//* Import the CSVCollaboratorManagementView component
import { CSVCollaboratorManagementView } from "../../UsersCollaboratorView/list/CSVManegementView";

type Collaborator = {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  updatedAt: string;
};

type User = {
  role: UserRole;
};

var debounce = require("lodash.debounce");

export function UsersCollaboratorView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  const [me, setMe] = useState<User | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [collaboratorToDelete, setCollaboratorToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showCSVCollaboratorModal, setShowCSVCollaboratorModal] = useState(false);
  const [totalCollaborators, setTotalCollaborators] = useState(0);

  const {
    collaborator,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteCollaborator,
    refetch,
    changePage,
    isDeleting,
  } = useCollaborator(token);

  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token]);

  useEffect(() => {
    setTotalCollaborators(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (collaboratorToDelete) {
      try {
        const currentPageItems = collaborator.length;
        const currentPage = pagination.currentPage;

        await deleteCollaborator(collaboratorToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setCollaboratorToDelete(null);
      } catch (error) {
        console.error("Error deleting collaborator:", error);
      }
    }
  }, [
    collaboratorToDelete,
    deleteCollaborator,
    collaborator.length,
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setCollaboratorToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  const onImportClick = () => {
    setShowCSVCollaboratorModal(true);
  };

  if (!me || isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        hideManager
        showPermissions
        search_text="Pesquise pelo nome ou email do colaboradores"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        additionalButtons={
          []
        }
        title={`Colaboradores (Total: ${totalCollaborators})`}
        not_found_message="Nenhum colaboradores encontrado"
        button_link="/users-collaborator/create"
        button_text="Adicionar colaboradores"
        deleting_message="Ao deletar este colaboradores, ele não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/users-collaborator/edit/${id}`)}
        onPermissionActionClick={(id) =>
          navigate(`/users-collaborator/permissions/${id}`)
        }
        onChangePagination={handleChangePagination}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={collaborator.map((collaborator: Collaborator) => ({
          ...collaborator,
          key: collaborator.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "email", type: "text", header: "Email" },
          { name: "createdAt", type: "date", header: "Criado em" },
        ]}
        loading_search={isSearching}
      />
      {showCSVCollaboratorModal && (
        <CSVCollaboratorManagementView
          token={token}
          onClose={() => setShowCSVCollaboratorModal(false)}
          refetch={refetch}
        />
      )}
    </div>
  );
}
